/**
 * Utility functions for formatting dates and times in EU format
 */

/**
 * Format a date as dd/mm/yyyy
 * @param date - Date object, ISO string, or date string
 * @returns Formatted date string in dd/mm/yyyy format
 */
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format a time as hh:mm (24-hour format)
 * @param time - Date object, ISO string, or time string (HH:MM:SS format)
 * @returns Formatted time string in hh:mm format
 */
export const formatTime = (time: Date | string): string => {
  if (typeof time === 'string') {
    // If it's a time string like "14:30:00", extract hours and minutes
    const timeParts = time.split(':');
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    // Otherwise, try to parse as date
    const dateObj = new Date(time);
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  
  const hours = time.getHours().toString().padStart(2, '0');
  const minutes = time.getMinutes().toString().padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

/**
 * Format a datetime as dd/mm/yyyy hh:mm
 * @param datetime - Date object or ISO string
 * @returns Formatted datetime string in dd/mm/yyyy hh:mm format
 */
export const formatDateTime = (datetime: Date | string): string => {
  const dateObj = typeof datetime === 'string' ? new Date(datetime) : datetime;
  
  return `${formatDate(dateObj)} ${formatTime(dateObj)}`;
};

/**
 * Format a pickup window with date and time range
 * @param date - Date object or date string
 * @param startTime - Start time string (HH:MM:SS format) or Date object
 * @param endTime - End time string (HH:MM:SS format) or Date object
 * @param fromText - Translation for "from" (default: "from")
 * @param toText - Translation for "to" (default: "to")
 * @returns Formatted string like "25/12/2025 from 14:00 to 16:00"
 */
export const formatPickupWindow = (
  date: Date | string,
  startTime: Date | string,
  endTime: Date | string,
  fromText: string = 'from',
  toText: string = 'to'
): string => {
  const formattedDate = formatDate(date);
  const formattedStartTime = formatTime(startTime);
  const formattedEndTime = formatTime(endTime);
  
  return `${formattedDate} ${fromText} ${formattedStartTime} ${toText} ${formattedEndTime}`;
};

/**
 * Format input date for HTML date input (yyyy-mm-dd)
 * @param date - Date object or ISO string
 * @returns Formatted date string in yyyy-mm-dd format
 */
export const formatDateForInput = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Format input time for HTML time input (HH:MM)
 * @param time - Date object, ISO string, or time string
 * @returns Formatted time string in HH:MM format
 */
export const formatTimeForInput = (time: Date | string): string => {
  if (typeof time === 'string') {
    // If it's already in HH:MM or HH:MM:SS format, extract HH:MM
    const timeParts = time.split(':');
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    // Otherwise, parse as date
    const dateObj = new Date(time);
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  
  const hours = time.getHours().toString().padStart(2, '0');
  const minutes = time.getMinutes().toString().padStart(2, '0');
  
  return `${hours}:${minutes}`;
};
